<template>
  <div class="google-data-step">
    <div class="mb-2 mainColor--text font-weight-bold" style="font-size: 1.35rem;">
      Connect Google Search Console
    </div>
    <p class="mb-6 mainColor--text" style="font-size: 0.95rem; line-height: 1.5;">
      This lets us see which keywords your site already ranks for, so we can build content plans.
    </p>

    <google-login class="mb-6" style="max-width: initial;" />

    <a
      class="help-gsc-link d-inline-flex align-center gap5 mb-4"
      href="https://help.pageoptimizer.pro/article/connect-google-search-console-to-pop"
      target="_blank"
      rel="noopener noreferrer"
    >
      <i class="material-icons-outlined" style="font-size: 18px;">help_outline</i>
      How to connect GSC
    </a>

    <div class="location-lang-domain-area">
      <div class="fields-grid">
        <div class="mb-5">
          <div class="card-small-title mb-1">
            Select location <span style="color: red;">*</span>
          </div>
          <v-autocomplete
            outlined
            hide-details="auto"
            :items="availableLocations"
            item-text="location_name"
            item-value="location_code"
            :value="config.wizardLocation"
            color="grayColor"
            @input="value => { setConfig('wizardLocation', value); emitReadiness() }"
          />
          <div class="mt-2 d-flex align-center gap5">
            <vue-toggles
              :value="config.wizardIsCountryEnabled"
              @click="() => { setConfig('wizardIsCountryEnabled', !config.wizardIsCountryEnabled); emitReadiness() }"
              width="40"
              height="20"
              :checkedBg="$vuetify.theme.currentTheme['green7Color']"
              :uncheckedBg="$vuetify.theme.currentTheme['grayColor']"
            />
            <div class="mainColor--text font-weight-bold">Enable country</div>
          </div>
        </div>
        <div class="mb-5">
          <div class="card-small-title mb-1">
            Select language <span style="color: red;">*</span>
          </div>
          <v-autocomplete
            outlined
            hide-details
            :items="targetLanguages"
            :value="config.wizardLanguage"
            item-text="language_name"
            item-value="language_code"
            color="grayColor"
            @input="value => { setConfig('wizardLanguage', value); emitReadiness() }"
          />
        </div>
        <div class="mb-5">
          <div class="card-small-title mb-1">
            Select your domain from GSC <span style="color: red;">*</span>
          </div>
          <domain-selector
            :base-domain="campaign?.targetUrl || ''"
            :initial-domain="config.wizardDomain || campaign?.targetUrl || config.wizardPODomain"
            @onChangeDomain="(d) => {
              googleSiteUrl = d;
              setConfig('wizardDomain', d);
              emitReadiness();
            }"
          />
        </div>
        <div class="mb-8">
          <div class="card-small-title mb-1">
            Date period
          </div>
          <v-autocomplete
            outlined
            hide-details
            :items="availablePeriods"
            :value="config.wizardPeriod"
            item-text="label"
            color="grayColor"
            @change="(value) => { setConfig('wizardPeriod', availablePeriods.find(period => period.value === value)); emitReadiness() }"
          />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import GoogleLogin from '@/components/Campaigns/GoogleLogin.vue'
import DomainSelector from '@/components/Campaigns/DomainSelector.vue'

import languages from '@/consts/dataforseo_languages.json'

import dataForSeoLocations from '@/consts/dataforseo_locations.json'

import gscAuditImportMixin from './gscAuditImportMixin.js'

/**
 * White Glove audit onboarding "Connect Google" step. The import pipeline itself lives in
 * gscAuditImportMixin.js (shared with the POP Authority setup wizard); this component owns the
 * White Glove selection UI and the pre-fill from an existing Wins connection.
 */
export default {
  props: ['site', 'activeAudit'],
  components: {
    GoogleLogin,
    DomainSelector
  },
  mixins: [gscAuditImportMixin],
  computed: {
    availableLocations () {
      return dataForSeoLocations.locations
    },
    targetLanguages () {
      return languages
    },
    googleUser () {
      return this.$store.state.googleUser
    },
    targetUrl () {
      if (this.campaign) {
        return this.campaign.targetUrl
      }
      return ''
    },
    canProceed () {
      return Boolean(this.googleUser && this.googleSiteUrl && this.config.wizardLocation && this.config.wizardLanguage)
    },
  },
  watch: {
    canProceed: {
      handler () {
        this.emitReadiness()
      },
      immediate: true,
    },
    googleUser: {
      handler () {
        this.emitReadiness()
      },
    },
    googleSiteUrl: {
      handler () {
        this.emitReadiness()
      },
    },
    submitting (val) {
      this.$emit('submitting-change', val)
    },
    // sub-account (mission control) relation is restored asynchronously after load
    '$store.state.selectedSubAccountRelation.id' () {
      this.prefillFromWinsGscConnection()
    },
  },
  methods: {
    /**
     * A GSC connection made in the Wins Settings tab is the shared source of
     * truth; pre-fill this step's selections from it so the flows agree.
     */
    prefillFromWinsGscConnection () {
      if (!this.site || !this.site.id) return
      this.$store.dispatch('getGscConnection', this.site.id)
        .then((response) => {
          const connection = response.data && response.data.connection
          this.hasActiveWinsConnection = Boolean(connection && connection.status === 'connected')
          if (!connection || connection.status !== 'connected') return
          const config = this.$store.getters.insightKeywordsConfig || {}
          if (!config.wizardDomain && connection.propertyUrl) {
            this.setConfig('wizardDomain', connection.propertyUrl)
            this.googleSiteUrl = connection.propertyUrl
          }
          if (!config.wizardLocation && connection.locationCode) {
            const code = parseInt(connection.locationCode, 10)
            this.setConfig('wizardLocation', Number.isFinite(code) ? code : connection.locationCode)
          }
          if (!config.wizardLanguage && connection.languageCode) {
            this.setConfig('wizardLanguage', connection.languageCode)
          }
        })
        .catch(() => {})
    },
  },
  mounted () {
    this.loadDomainLevelCampaign()
    this.prefillFromWinsGscConnection()
  },
}
</script>

<style lang="scss" scoped>
.google-data-step {
  .fields-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 24px;

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
    }
  }

  .help-gsc-link {
    color: var(--v-mainColor-base);
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
  }
}
</style>
